import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { VariantColor } from '@repo/toolkit/colors'
import { SVGFC } from '@repo/toolkit/types'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import { PropsWithChildren, useEffect } from 'react'

import SidebarData from '../../../../../apps/backoffice/src/components/elements/sidebar/sidebar-data'
import useAuth from '../../../../../apps/backoffice/src/lib/hooks/use-auth'
import useSidebarManager from '../../../../../apps/backoffice/src/lib/hooks/use-sidebar-manager'
import { LogoutIcon, MenuBurgerIcon } from '../../../icons'
import Badge from '../../atoms/badge'
import Typography from '../../atoms/typography'

interface NavbarProps {
  title: string | React.ReactNode
  icon: SVGFC
  badgeTitle?: string
  badgeVariant?: VariantColor
  phrase?: string
  phraseVariant?: VariantColor
  onIconClick?: () => void
}

// Inside your Navbar component
const Navbar: React.FC<PropsWithChildren<NavbarProps>> = ({
  title,
  icon,
  children,
  badgeTitle,
  badgeVariant = 'primary',
  phrase,
  phraseVariant = 'primary',
  onIconClick
}) => {
  const { user, logout } = useAuth()
  const device = useBreakPoint()
  const isMobile = device === 'mobile'
  const { isSidebarExpanded, handleSidebarExpansion } = useSidebarManager()

  const toggleSidebar = () => {
    handleSidebarExpansion(!isSidebarExpanded)
  }

  // Disable/Enable body scroll when sidebar state changes
  useEffect(() => {
    if (isSidebarExpanded) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = ''
    }
  }, [isSidebarExpanded])

  const handleTouchStart = (e: React.TouchEvent) => {
    const touchStartX = e.touches[0].clientX

    const handleTouchMove = (moveEvent: TouchEvent) => {
      const touchMoveX = moveEvent.touches[0].clientX
      if (touchMoveX < touchStartX - 50) {
        handleSidebarExpansion(false)
        document.removeEventListener('touchmove', handleTouchMove)
      }
    }

    document.addEventListener('touchmove', handleTouchMove)

    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }

    document.addEventListener('touchend', handleTouchEnd)
  }

  return (
    <>
      {/* Navbar */}
      <div className="w-full rounded-2xl py-6 px-5 bg-white flex flex-row justify-between">
        {isMobile && (
          <div onClick={toggleSidebar}>
            <MenuBurgerIcon color={tailwindColors.grey.DEFAULT} height="32" width="32" />
          </div>
        )}
        <div className="flex flex-row mobile:justify-center  items-center gap-2 w-full">
          <div className="flex items-center gap-2">
            <div
              className={cn('rounded p-1 bg-secondary-light flex items-center justify-center', {
                'cursor-pointer hover:bg-secondary-light-hover': Boolean(onIconClick)
              })}
              onClick={onIconClick}
            >
              {icon?.({
                color: tailwindColors.secondary.DEFAULT,
                width: '24px',
                height: '24px'
              })}
            </div>
            {typeof title === 'string' ? (
              <Typography variant={title.length > 20 && isMobile ? 'tag' : 'h3'}>
                {title}
              </Typography>
            ) : (
              title
            )}
            {phrase && (
              <Typography variant={'h3'} color={phraseVariant}>
                {phrase}
              </Typography>
            )}
            {badgeTitle && <Badge variant={badgeVariant} text={badgeTitle} />}
          </div>
        </div>

        <div className="min-w-fit">{children}</div>
      </div>

      {/* Sidebar */}
      {isMobile && (
        <>
          <div
            onTouchStart={handleTouchStart}
            className={cn(
              'fixed top-0 left-0 h-full w-80 py-10 -translate-x-full bg-primary shadow-lg transform transition-transform duration-300 ease-in-out z-50 rounded-br-2xl rounded-tr-2xl',
              { 'translate-x-0': isSidebarExpanded }
            )}
          >
            <div className="flex flex-col justify-between h-full">
              {user && (
                <SidebarData
                  logo={<img src="/logo.png" alt="Logo" className="object-contain w-24" />}
                  customButton={
                    <div className="flex gap-2 p-2 items-center bg-primary-dark rounded">
                      <LogoutIcon color={tailwindColors.white.DEFAULT} />
                      <button type="button" onClick={logout}>
                        <Typography variant="label-medium" color={tailwindColors.white.DEFAULT}>
                          Déconnexion
                        </Typography>
                      </button>
                    </div>
                  }
                />
              )}
            </div>
          </div>
          {/* Overlay to close sidebar */}
          {isSidebarExpanded && (
            <div className="fixed inset-0 bg-black opacity-50 z-40" onClick={toggleSidebar}></div>
          )}
        </>
      )}
    </>
  )
}

export default Navbar
